// Přehled pohledů na úvodní stránce: mřížka 6 sloupců s editorem přímo na místě.
// V režimu úprav jde kartu přetáhnout jinam, za pravý okraj roztáhnout (2–6 sloupců), skrýt a skryté zase přidat.
// Klávesnice: Tab na kartu, šipky vlevo/vpravo posun, Shift + šipky šířka, Delete skrýt.
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as RPointerEvent } from 'react';
import { EyeSlashIcon, PlusIcon, SlidersHorizontalIcon } from '@phosphor-icons/react';
import { IconButton, useToast } from '@smworks-cz/ui-kit';
import { VIEW_GRID, viewCols, type ViewDTO } from '@sam/shared';
import { ViewTile } from '../pages/View';
import { navigate, viewPath } from '../lib/router';
import { reorderViews, updateView } from '../lib/views';

interface Drag {
  id: string;
  pointerId: number;
  startX: number;
  startY: number;
  x: number;
  y: number;
  w: number;
  h: number;
  offX: number;
  offY: number;
  moved: boolean;
}

interface Resize {
  id: string;
  left: number;
  cols: number;
}

const clamp = (n: number) => Math.min(VIEW_GRID.maxCols, Math.max(VIEW_GRID.minCols, n));

export function ViewsBoard({ views }: { views: ViewDTO[] }) {
  const [editing, setEditing] = useState(false);
  const [order, setOrder] = useState<string[] | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [resize, setResize] = useState<Resize | null>(null);
  const grid = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const fail = (e: unknown) => toast({ variant: 'error', title: (e as Error).message || 'Změnu se nepodařilo uložit.' });

  const visible = views.filter((v) => v.onHome && !v.locked);
  const hidden = views.filter((v) => !v.onHome && !v.locked);
  const byId = new Map(visible.map((v) => [v.id, v]));
  const ids = order ?? visible.map((v) => v.id);
  const tiles = ids.map((id) => byId.get(id)).filter((v): v is ViewDTO => !!v);
  const colsOf = (v: ViewDTO) => (resize?.id === v.id ? resize.cols : viewCols(v));

  useEffect(() => {
    if (!editing) return;
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && setEditing(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  const saveOrder = (next: string[]) => {
    const rest = views.map((v) => v.id).filter((id) => !next.includes(id));
    void reorderViews([...next, ...rest]).catch(fail);
  };
  const setCols = (v: ViewDTO, cols: number) => {
    if (cols !== viewCols(v) || v.cols === null) void updateView(v.id, { cols }).catch(fail);
  };
  const hide = (v: ViewDTO) => void updateView(v.id, { onHome: false }).catch(fail);

  /* ---------- přetažení karty ---------- */

  const onTileDown = (e: RPointerEvent<HTMLDivElement>, v: ViewDTO) => {
    if (!editing || e.button !== 0 || (e.target as HTMLElement).closest('button, .tile-resize')) return;
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
    setOrder(visible.map((x) => x.id));
    setDrag({
      id: v.id,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      w: r.width,
      h: r.height,
      offX: e.clientX - r.left,
      offY: e.clientY - r.top,
      moved: false,
    });
  };

  const onTileMove = (e: RPointerEvent<HTMLDivElement>) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const moved = drag.moved || Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > 4;
    setDrag({ ...drag, x: e.clientX, y: e.clientY, moved });
    if (!moved) return;
    // Karta pod ukazatelem (mimo tu tažennou): vložit před ni, nebo za ni podle poloviny.
    const over = document
      .elementsFromPoint(e.clientX, e.clientY)
      .map((el) => (el as HTMLElement).closest<HTMLElement>('.tile[data-id]'))
      .find((el) => el && el.dataset.id !== drag.id);
    if (!over) return;
    const r = over.getBoundingClientRect();
    const after = e.clientX > r.left + r.width / 2;
    setOrder((prev) => {
      const cur = prev ?? ids;
      const list = cur.filter((id) => id !== drag.id);
      const i = list.indexOf(over.dataset.id!);
      list.splice(after ? i + 1 : i, 0, drag.id);
      return list.join() === cur.join() ? prev : list;
    });
  };

  const onTileUp = (e: RPointerEvent<HTMLDivElement>) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    if (order && order.join() !== visible.map((v) => v.id).join()) saveOrder(order);
    setDrag(null);
    setOrder(null);
  };

  /* ---------- roztažení za pravý okraj ---------- */

  const step = () => {
    const g = grid.current;
    if (!g) return 100;
    const gap = parseFloat(getComputedStyle(g).columnGap) || 14;
    return (g.clientWidth - gap * (VIEW_GRID.columns - 1)) / VIEW_GRID.columns + gap;
  };

  const onResizeDown = (e: RPointerEvent<HTMLSpanElement>, v: ViewDTO) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const tile = e.currentTarget.closest('.tile')!.getBoundingClientRect();
    setResize({ id: v.id, left: tile.left, cols: viewCols(v) });
  };
  const onResizeMove = (e: RPointerEvent<HTMLSpanElement>) => {
    if (!resize) return;
    const cols = clamp(Math.round((e.clientX - resize.left) / step()));
    if (cols !== resize.cols) setResize({ ...resize, cols });
  };
  const onResizeUp = (v: ViewDTO) => {
    if (!resize) return;
    setCols(v, resize.cols);
    setResize(null);
  };

  /* ---------- klávesnice ---------- */

  const onTileKey = (e: KeyboardEvent<HTMLDivElement>, v: ViewDTO) => {
    if (!editing || e.target !== e.currentTarget) return;
    const i = ids.indexOf(v.id);
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const dir = e.key === 'ArrowLeft' ? -1 : 1;
      if (e.shiftKey) return setCols(v, clamp(viewCols(v) + dir));
      const j = i + dir;
      if (j < 0 || j >= ids.length) return;
      const next = [...ids];
      [next[i], next[j]] = [next[j], next[i]];
      saveOrder(next);
      requestAnimationFrame(() => grid.current?.querySelector<HTMLElement>(`.tile[data-id="${v.id}"]`)?.focus());
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      hide(v);
    }
  };

  if (!views.some((v) => !v.locked)) return null;
  const dragged = drag?.moved ? tiles.find((v) => v.id === drag.id) : undefined;

  return (
    <section className={`board${editing ? ' editing' : ''}`}>
      <div className="board-h">
        <p className="h2">Moje pohledy</p>
        <IconButton
          icon={<SlidersHorizontalIcon size={15} />}
          label={editing ? 'Ukončit úpravy přehledu' : 'Upravit přehled'}
          tooltip={editing ? 'Hotovo' : 'Upravit přehled'}
          variant="ghost"
          size="xs"
          active={editing}
          onClick={() => setEditing((x) => !x)}
        />
        {editing && (
          <>
            <span className="board-hint">Přetáhněte kartu jinam, za pravý okraj ji roztáhnete.</span>
            <button type="button" className="board-done" onClick={() => setEditing(false)}>
              Hotovo
            </button>
          </>
        )}
      </div>

      {tiles.length > 0 && (
        <div className="tiles" ref={grid}>
          {tiles.map((v) => {
            const cols = colsOf(v);
            return (
              <div
                key={v.id}
                data-id={v.id}
                data-cols={cols}
                className={`tile${drag?.id === v.id && drag.moved ? ' is-dragging' : ''}${resize?.id === v.id ? ' is-resizing' : ''}`}
                style={{ gridColumn: `span ${cols}` }}
                tabIndex={editing ? 0 : undefined}
                aria-label={editing ? `${v.title}, šířka ${cols} ze 6. Šipky posun, Shift a šipky šířka, Delete skrýt.` : undefined}
                onPointerDown={(e) => onTileDown(e, v)}
                onPointerMove={onTileMove}
                onPointerUp={onTileUp}
                onPointerCancel={onTileUp}
                onKeyDown={(e) => onTileKey(e, v)}
              >
                <ViewTile id={v.id} title={v.title} cols={cols} onOpen={editing ? undefined : () => navigate(viewPath(v.id))} />
                {editing && (
                  <>
                    <div className="tile-tools">
                      <span className="tile-cols">{cols}/6</span>
                      <button type="button" className="tile-tool" onClick={() => hide(v)} aria-label={`Skrýt ${v.title} z přehledu`} title="Skrýt z přehledu">
                        <EyeSlashIcon size={15} />
                      </button>
                    </div>
                    <span
                      className="tile-resize"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Šířka karty ${v.title}`}
                      title="Táhnutím změníte šířku, dvojklikem vrátíte automatickou"
                      onPointerDown={(e) => onResizeDown(e, v)}
                      onPointerMove={onResizeMove}
                      onPointerUp={() => onResizeUp(v)}
                      onPointerCancel={() => setResize(null)}
                      onDoubleClick={() => void updateView(v.id, { cols: null }).catch(fail)}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!tiles.length && !editing && (
        <p className="muted small">
          Všechny pohledy jsou skryté.{' '}
          <button type="button" className="link-btn" onClick={() => setEditing(true)}>
            Upravit přehled
          </button>
        </p>
      )}

      {editing && hidden.length > 0 && (
        <div className="board-hidden">
          <span className="muted small">Skryté:</span>
          {hidden.map((v) => (
            <button key={v.id} type="button" className="board-chip" onClick={() => void updateView(v.id, { onHome: true }).catch(fail)}>
              <PlusIcon size={13} weight="bold" />
              {v.title}
            </button>
          ))}
        </div>
      )}

      {dragged && drag && (
        <div
          className="tile-ghost"
          style={{ width: drag.w, height: Math.min(drag.h, 200), left: drag.x - drag.offX, top: drag.y - Math.min(drag.offY, 100) }}
          aria-hidden="true"
        >
          {dragged.title}
        </div>
      )}
    </section>
  );
}
